import React, { useRef } from "react";
import "./ListPagination.scss";

const PAGE_WINDOW = 5;

// Reset scroll to top after a page change. The list scrolls inside an overflow
// container on some admin pages (e.g. users) and the document on others (e.g.
// products), so cover both: the nearest scrollable ancestor plus the document.
const scrollPagerToTop = (el) => {
  let node = el?.parentElement;
  while (node) {
    const overflowY = getComputedStyle(node).overflowY;
    if (
      (overflowY === "auto" ||
        overflowY === "scroll" ||
        overflowY === "overlay") &&
      node.scrollHeight > node.clientHeight
    ) {
      node.scrollTop = 0;
      break;
    }
    node = node.parentElement;
  }
  window.scrollTo(0, 0);
  if (document.scrollingElement) document.scrollingElement.scrollTop = 0;
};

export const getPageWindow = (totalPages, activePageIndex) => {
  if (totalPages <= PAGE_WINDOW) {
    return { start: 0, end: totalPages };
  }

  const start = Math.min(
    Math.max(0, activePageIndex - 2),
    totalPages - PAGE_WINDOW,
  );
  return { start, end: start + PAGE_WINDOW };
};

const activateOnKeyDown = (event, action) => {
  if (event.key === "Enter" || event.key === " ") {
    event.preventDefault();
    action();
  }
};

const clickableControlProps = (onActivate) => ({
  role: "button",
  tabIndex: 0,
  onClick: onActivate,
  onKeyDown: (event) => activateOnKeyDown(event, onActivate),
});

const ListPagination = ({
  className = "",
  showPagination,
  toShow,
  toShowFun,
  cFrom,
  cTo,
  pCounter,
  showPaggination,
  downPaggination,
  UpPaggination,
}) => {
  const totalPages = pCounter.length;
  const { start: pageWindowStart, end: pageWindowEnd } = getPageWindow(
    totalPages,
    cFrom,
  );

  const rootRef = useRef(null);

  // Wrap each page-change action so the scroll position also resets to top
  // (after the re-render, so the new page's first rows land at the top).
  const withScrollTop =
    (action) =>
    (...args) => {
      action(...args);
      const el = rootRef.current;
      scrollPagerToTop(el);
      requestAnimationFrame(() => scrollPagerToTop(el));
    };

  return (
    <div
      ref={rootRef}
      className={`list-pagination pagination ${className}`.trim()}
    >
      <ul className="list-pagination__sizes">
        <li
          {...clickableControlProps(withScrollTop(() => toShowFun(25)))}
          className={toShow === 25 ? "active" : null}
          aria-current={toShow === 25 ? "true" : undefined}
        >
          25
        </li>
        <li
          {...clickableControlProps(withScrollTop(() => toShowFun(50)))}
          className={toShow === 50 ? "active" : null}
          aria-current={toShow === 50 ? "true" : undefined}
        >
          50
        </li>
        <li
          {...clickableControlProps(withScrollTop(() => toShowFun(100)))}
          className={toShow === 100 ? "active" : null}
          aria-current={toShow === 100 ? "true" : undefined}
        >
          100
        </li>
      </ul>
      {showPagination ? (
        <ul className="list-pagination__pages">
          {cFrom > 0 ? (
            <li
              {...clickableControlProps(withScrollTop(downPaggination))}
              aria-label="עמוד קודם"
            >
              <span>{"<"}</span>
            </li>
          ) : null}
          {totalPages
            ? pCounter.map((element, index) =>
                index >= pageWindowStart && index < pageWindowEnd ? (
                  <li
                    key={index}
                    {...(cFrom !== index
                      ? clickableControlProps(
                          withScrollTop(() => showPaggination(element)),
                        )
                      : { "aria-current": "page" })}
                    className={cFrom === index ? "active" : null}
                  >
                    <span>{element + 1}</span>
                  </li>
                ) : null,
              )
            : null}
          {cFrom < totalPages - 1 ? (
            <li
              {...clickableControlProps(withScrollTop(UpPaggination))}
              aria-label="עמוד הבא"
            >
              <span>{">"}</span>
            </li>
          ) : null}
        </ul>
      ) : null}
    </div>
  );
};

export default ListPagination;
